// src/router/index.js
import { createRouter, createWebHistory } from 'vue-router'
import { logoutIfExpired, isAuthenticated } from '../utils/auth'
import { useEntitlements } from '../composables/useEntitlements'
import {
  isPostAuthRoutePath,
  normalizePostAuthPath,
} from '../newui/router/postAuthRouteCatalog'

// Lazy-loaded route components for code splitting
const LoginPage = () => import('../views/LoginPage.vue')
const LogoutPage = () => import('../views/LogoutPage.vue')
const RegisterPage = () => import('../views/RegisterPage.vue')
const ForgotPassword = () => import('../views/ForgotPassword.vue')
const ResetPassword = () => import('../views/ResetPassword.vue')

const ProspectInfoPage = () => import('../views/nda/ProspectInfoPage.vue')
const NdaReviewPage = () => import('../views/nda/NdaReviewPage.vue')
const NdaVerificationPage = () => import('../views/nda/NdaVerificationPage.vue')
const NdaIntakeV2Page = () => import('../views/nda/NdaIntakeV2Page.vue')
const NdaSendPage = () => import('../views/nda/NdaSendPage.vue')
const NdaStatusPage = () => import('../views/nda/NdaStatusPage.vue')
const OfferingMemorandumPage = () => import('../views/nda/OfferingMemorandumPage.vue')
const OfferingMemorandumShare = () => import('../views/OfferingMemorandumShare.vue')

const PaywallPage = () => import('../views/payments/PaywallPage.vue')
const SoloPlan = () => import('../views/payments/SoloPlan.vue')
const SoloPlanAnnual = () => import('../views/payments/SoloPlanAnnual.vue')
const GrowthPlan = () => import('../views/payments/GrowthPlan.vue')
const GrowthPlanAnnual = () => import('../views/payments/GrowthPlanAnnual.vue')
const StandardPlan = () => import('../views/payments/StandardPlan.vue')
const StandardPlanAnnual = () => import('../views/payments/StandardPlanAnnual.vue')
const ProPlan = () => import('../views/payments/ProPlan.vue')
const ProPlanAnnual = () => import('../views/payments/ProPlanAnnual.vue')
const BillingManagement = () => import('../views/payments/BillingManagement.vue')
const SubscribeSuccess = () => import('../views/payments/SubscribeSuccess.vue')
const ClaimStart = () => import('../views/payments/ClaimStart.vue')
const ClaimComplete = () => import('../views/payments/ClaimComplete.vue')

const normalizePath = normalizePostAuthPath

const isPostAuthReactPath = isPostAuthRoutePath

const routes = [
  { path: '/', redirect: '/login' },
  { path: '/login', name: 'Login', component: LoginPage },
  { path: '/register', name: 'Register', component: RegisterPage },
  { path: '/forgot-password', name: 'ForgotPassword', component: ForgotPassword },
  { path: '/reset-password', name: 'ResetPassword', component: ResetPassword },
  { path: '/logout', name: 'Logout', component: LogoutPage },

  // NDA and public share flows remain Vue-driven
  { path: '/nda/prospect-info', name: 'ProspectInfo', component: ProspectInfoPage },
  { path: '/nda/review', name: 'NdaReview', component: NdaReviewPage },
  { path: '/nda/verify', name: 'NdaVerify', component: NdaVerificationPage },
  { path: '/nda/v2/intake/:token', name: 'NdaIntakeV2', component: NdaIntakeV2Page },
  { path: '/nda/send', name: 'NdaSend', component: NdaSendPage },
  { path: '/nda/status', name: 'NdaStatus', component: NdaStatusPage },
  { path: '/nda/offering', name: 'OfferingMemorandum', component: OfferingMemorandumPage },
  { path: '/om-share/:token', name: 'OfferingMemorandumShare', component: OfferingMemorandumShare },

  // Subscription/paywall flows remain Vue-driven
  { path: '/paywall', name: 'Paywall', component: PaywallPage },
  { path: '/pricing', name: 'Pricing', component: PaywallPage },
  { path: '/subscribe', name: 'Subscribe', component: PaywallPage },
  { path: '/subscribe/success', name: 'SubscribeSuccess', component: SubscribeSuccess },
  { path: '/claim/start', name: 'ClaimStart', component: ClaimStart },
  { path: '/claim', name: 'ClaimComplete', component: ClaimComplete },
  { path: '/solo-plan', name: 'SoloPlan', component: SoloPlan },
  { path: '/solo-plan-annual', name: 'SoloPlanAnnual', component: SoloPlanAnnual },
  { path: '/growth-plan', name: 'GrowthPlan', component: GrowthPlan },
  { path: '/growth-plan-annual', name: 'GrowthPlanAnnual', component: GrowthPlanAnnual },
  { path: '/standard-plan', name: 'StandardPlan', component: StandardPlan },
  { path: '/standard-plan-annual', name: 'StandardPlanAnnual', component: StandardPlanAnnual },
  { path: '/pro-plan', name: 'ProPlan', component: ProPlan },
  { path: '/pro-plan-annual', name: 'ProPlanAnnual', component: ProPlanAnnual },
  { path: '/payments/billing', name: 'BillingManagement', component: BillingManagement },

]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach(async (to, from, next) => {
  if (logoutIfExpired()) {
    return
  }

  // Prevent navigating back to the NDA review once it has been signed
  const signed = sessionStorage.getItem('signedNda')
  if (to.name === 'NdaReview' && signed) {
    try {
      const saved = JSON.parse(signed)
      if (!to.query.ndaId || String(to.query.ndaId) === String(saved.ndaId)) {
        next({ name: 'OfferingMemorandum', query: saved })
        return
      }
    } catch (e) {
      // ignore malformed session storage
    }
  }

  const publicRoutes = [
    '/login',
    '/register',
    '/forgot-password',
    '/reset-password',
    '/logout',
    '/logged-out',
    '/callback',
    '/claim/start',
    '/claim',
    '/nda/prospect-info',
    '/nda/review',
    '/nda/verify',
    '/nda/send',
    '/nda/status',
    '/nda/offering',
    '/nda/sign'
  ]

  const { fetchEntitlements, hasAppAccess } = useEntitlements()

  const normalizedPath = normalizePath(to.path)
  const isPublicShare = normalizedPath.startsWith('/om-share/') || normalizedPath.startsWith('/nda/v2/intake/')
  const isPostAuthRoute = isPostAuthReactPath(normalizedPath)

  // Post-auth auth handling is owned by React (PostAuthEntry/AuthGuardedSurface).
  // Vue should only guard non-post-auth routes here.
  if (!publicRoutes.includes(normalizedPath) && !isPublicShare && !isPostAuthRoute && !isAuthenticated()) {
    window.location.href = '/login'
    return
  }

  if (isAuthenticated() && ['/login', '/register', '/forgot-password', '/reset-password'].includes(normalizedPath)) {
    window.location.assign('/dashboard')
    return
  }

  // Post-auth entitlement handling is owned by React (PostAuthEntry/EntitlementGuardedSurface).
  if (isAuthenticated() && !isPostAuthRoute) {
    try {
      await fetchEntitlements()
    } catch (err) {
      // ignore and continue to gating logic
    }

    const gatedWhitelist = new Set([
      ...publicRoutes,
      '/pricing',
      '/subscribe',
      '/subscribe/success',
      '/claim/start',
      '/claim',
      '/payments/billing'
    ])

    if (!gatedWhitelist.has(normalizedPath) && !hasAppAccess.value) {
      next({ path: '/subscribe', query: { redirect: to.fullPath } })
      return
    }
  }

  next()
})

router.afterEach((to) => {
  if (to.meta.noScroll) {
    document.documentElement.classList.add('no-scroll')
  } else {
    document.documentElement.classList.remove('no-scroll')
  }
})

export default router
